/**
 * API Service - Centralized API client for all backend communication
 * Base URL configured from environment variables
 */
import { tokenManager } from '../utils/tokenManager';

// Default to local backend for development.
// In production, set REACT_APP_API_URL at build time:
//   REACT_APP_API_URL=https://api.kaammytra.com/api npm run build
const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Helper function to make API calls
 */
const apiCall = async (endpoint, options = {}) => {
  const token = tokenManager.getAccessToken();
  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Lightweight request debug log (safe to leave in dev)
  try {
    console.debug('API request', { endpoint, method: options.method || 'GET', body: options.body ? options.body : null });
  } catch (e) {
    // ignore logging errors
  }

  let response = await fetch(`${API_BASE_URL}${endpoint}`, {
    ...options,
    headers,
  });

  let data;
  let text;
  try {
    text = await response.text();
    data = text ? JSON.parse(text) : {};
  } catch (e) {
    data = { raw: text };
  }

  // If 404, retry by toggling the '/api' prefix
  if (!response.ok) {
    // Helpful debug: log request/response for 400-level errors to aid troubleshooting
    if (response.status >= 400 && response.status < 500) {
      try {
        console.error('API client debug - request failed', {
          endpoint,
          status: response.status,
          requestBody: options && options.body ? options.body : null,
          responseData: data,
          responseText: text,
        });
      } catch (logErr) {
        console.error('API debug logging failed', logErr);
      }
    }
    if (response.status === 404 && API_BASE_URL) {
      const hasApiSuffix = /\/api\/?$/.test(API_BASE_URL);
      const fallbackBase = hasApiSuffix
        ? API_BASE_URL.replace(/\/api\/?$/, '')
        : `${API_BASE_URL.replace(/\/$/, '')}/api`;

      try {
        const fallbackRes = await fetch(`${fallbackBase}${endpoint}`, {
          ...options,
          headers,
        });
        let fbText = await fallbackRes.text();
        let fbData;
        try { fbData = fbText ? JSON.parse(fbText) : {}; } catch (e) { fbData = { raw: fbText }; }
        if (!fallbackRes.ok) {
          const errorMsg = fbData.message || fbData.error || fbText || `API request failed with status ${fallbackRes.status}`;
          const error = new Error(errorMsg);
          error.status = fallbackRes.status;
          error.data = fbData;
          throw error;
        }
        return fbData;
      } catch (retryErr) {
        // fall through to throw original error below if retry fails
        console.warn('API fallback retry failed:', retryErr);
      }
    }

    const errorMsg = data.message || data.error || text || `API request failed with status ${response.status}`;
    const error = new Error(errorMsg);
    error.status = response.status;
    error.data = data;
    throw error;
  }

  return data;
};

/**
 * Authentication API calls
 */
export const authAPI = {
  register: (userData) =>
    apiCall('/auth/register', {
      method: 'POST',
      body: JSON.stringify(userData),
    }),

  login: (credentials) =>
    apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    }),

  refreshToken: (refreshToken) =>
    apiCall('/auth/refresh-token', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    }),

  logout: () =>
    apiCall('/auth/logout', {
      method: 'POST',
    }),

  getProfile: () =>
    apiCall('/auth/profile', {
      method: 'GET',
    }),

  changePassword: (passwordData) =>
    apiCall('/auth/change-password', {
      method: 'PUT',
      body: JSON.stringify(passwordData),
    }),
};

/**
 * Loan Types API calls
 */
export const loanTypesAPI = {
  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/loan-types?${queryString}`, {
      method: 'GET',
    });
  },

  get: (id) =>
    apiCall(`/loan-types/${id}`, {
      method: 'GET',
    }),

  getOptions: () =>
    apiCall('/loan-types/options', {
      method: 'GET',
    }),

  // Add alias for backward compatibility
  getTypes: () =>
    apiCall('/loan-types/options', {
      method: 'GET',
    }),
};

/**
 * Retailers API calls
 */
export const retailersAPI = {
  register: (retailerData) =>
    apiCall('/retailers', {
      method: 'POST',
      body: JSON.stringify(retailerData),
    }),

  list: async (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    const response = await apiCall(`/retailers?${queryString}`, {
      method: 'GET',
    });
    
    // Handle paginated response format
    if (response && response.data) {
      return response.data; // Return the retailers array from paginated response
    } else if (Array.isArray(response)) {
      return response;
    } else {
      return [];
    }
  },

  // Add alias for backward compatibility
  getRetailers: async (params = {}) => {
    return await retailersAPI.list(params);
  },

  get: (id) =>
    apiCall(`/retailers/${id}`, {
      method: 'GET',
    }),

  update: (id, retailerData) =>
    apiCall(`/retailers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(retailerData),
    }),

  delete: (id) =>
    apiCall(`/retailers/${id}`, {
      method: 'DELETE',
    }),

  uploadDocument: (retailerId, formData) =>
    fetch(`${API_BASE_URL}/retailers/${retailerId}/documents`, {
      method: 'POST',
      headers: {
        ...tokenManager.getAuthHeader(),
      },
      body: formData,
    }).then((res) => {
      if (!res.ok) {
        throw new Error('Failed to upload document');
      }
      return res.json();
    }),

  getDocuments: (id) =>
    apiCall(`/retailers/${id}/documents`, {
      method: 'GET',
    }),
};

/**
 * Loans API calls
 */
export const loansAPI = {
  submit: (loanData) =>
    apiCall('/loans', {
      method: 'POST',
      body: JSON.stringify(loanData),
    }),

  // Add alias for backward compatibility
  submitLoan: (loanData) =>
    apiCall('/loans', {
      method: 'POST',
      body: JSON.stringify(loanData),
    }),

  list: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/loans?${queryString}`, {
      method: 'GET',
    });
  },

  get: (id) =>
    apiCall(`/loans/${id}`, {
      method: 'GET',
    }),
};

/**
 * Collections API calls
 */
export const collectionsAPI = {
  record: (collectionData) =>
    apiCall('/collections', {
      method: 'POST',
      body: JSON.stringify(collectionData),
    }),

  getToday: () =>
    apiCall('/collections/today', {
      method: 'GET',
    }),

  getHistory: (loanId, params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/collections/${loanId}/history?${queryString}`, {
      method: 'GET',
    });
  },

  getStats: (loanId) =>
    apiCall(`/collections/${loanId}/stats`, {
      method: 'GET',
    }),

  getSummary: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/collections/summary/date?${queryString}`, {
      method: 'GET',
    });
  },

  getAgentHistory: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/collections/agent/history?${queryString}`, {
      method: 'GET',
    });
  },
};

/**
 * Reports API calls
 */
export const reportsAPI = {
  getAgentDashboard: () =>
    apiCall('/reports/dashboard/agent', {
      method: 'GET',
    }),

  getCollectionsReport: (params = {}) => {
    const queryString = new URLSearchParams(params).toString();
    return apiCall(`/reports/collections?${queryString}`, {
      method: 'GET',
    });
  },
};

/**
 * Agent API calls
 */
export const agentAPI = {
  getDashboard: () =>
    apiCall('/reports/dashboard/agent', {
      method: 'GET',
    }),

  getProfile: () =>
    apiCall('/auth/profile', {
      method: 'GET',
    }),
};

/**
 * KYC Verification API calls
 */
export const kycAPI = {
  // Send OTP for Aadhaar verification
  sendAadhaarOtp: (aadhaarId) =>
    apiCall('/kyc/aadhaar/send-otp', {
      method: 'POST',
      body: JSON.stringify({ aadhaarId }),
    }),

  // Verify Aadhaar with OTP
  verifyAadhaarOtp: (aadhaarId, otp, refId) =>
    apiCall('/kyc/aadhaar/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ aadhaarId, otp, refId }),
    }),

  // Get Aadhaar verification status
  getAadhaarStatus: (verificationId) =>
    apiCall(`/kyc/aadhaar/status/${verificationId}`, {
      method: 'GET',
    }),

  // Verify PAN card
  verifyPan: (panId) =>
    apiCall('/kyc/pan/verify', {
      method: 'POST',
      body: JSON.stringify({ panId }),
    }),

  // Get PAN verification status
  getPanStatus: (verificationId) =>
    apiCall(`/kyc/pan/status/${verificationId}`, {
      method: 'GET',
    }),
};

/**
 * Utility function to check API health
 */
export const checkHealth = () =>
  fetch(`${API_BASE_URL}/health`)
    .then((res) => res.json())
    .catch(() => ({ status: 'offline' }));

export default {
  authAPI,
  loanTypesAPI,
  retailersAPI,
  loansAPI,
  collectionsAPI,
  reportsAPI,
  agentAPI,
  kycAPI,
  checkHealth
};
